import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UI, DISPLAY } from '../styles/tl5';
import { ROUTES, buildRoute } from '../constants/routes';
import {
  useListCentresQuery,
  useListPopularTestsQuery,
} from '../store/siteContentApi';

// City — "Tests in your city", built to the v5 reference.
//
// Four sections: the white hero card with the city switcher, popular
// tests, Care Centres beside home-collection areas, and city FAQs.
//
// The reference hardcodes seven cities with invented ETAs, invented
// locality lists and `₹ [price]` / `[4.x] ★` placeholders. Ours is built
// off what we actually have:
//
//   · cities and branches come from GET /centres — 13 real cities, not
//     the reference's seven. The Care Centres card is real.
//   · popular tests come from GET /popular-tests, with real prices, so
//     the `₹ [price]` placeholder goes away.
//   · the reference's per-city ETA ("45 min" in Hyderabad, "60 min"
//     elsewhere) has no backing field. The site already publishes 60
//     minutes on About, Cart, Account and the home banners, so that is
//     what this says too, everywhere. API-NOTES 2.27.
//   · the `[4.x] ★ [n] Google reviews` tile has no data at all (2.15).
//     It is replaced with the reportable-test count, which is real.
//   · the locality pills are labelled "placeholder, to be confirmed" in
//     the reference itself. We have no area data for any city, so the
//     card keeps its place and asks for a pincode instead of listing
//     made-up neighbourhoods. API-NOTES 2.27.
//
// /centres has no state or region column (2.13), so the state pill uses
// the small lookup below — plain geography for the thirteen cities we
// actually operate in, and the pill is simply not rendered for a city
// that isn't in it.

const STATE_BY_CITY = {
  Anantapur: 'Andhra Pradesh',
  Bengaluru: 'Karnataka',
  Chandigarh: 'Chandigarh',
  Guntur: 'Andhra Pradesh',
  Hanumakonda: 'Telangana',
  Hyderabad: 'Telangana',
  Jammu: 'Jammu & Kashmir',
  Karimnagar: 'Telangana',
  Mahbubnagar: 'Telangana',
  Nizamabad: 'Telangana',
  Noida: 'Uttar Pradesh',
  Vijayawada: 'Andhra Pradesh',
  Visakhapatnam: 'Andhra Pradesh',
};

const ARRIVAL = '60 min';

// ── shared fragments ──────────────────────────────────────────────────
const h2 = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(28px, 3.4vw, 40px)',
  letterSpacing: '-.03em',
  color: 'var(--tl-g9)',
};

const cardH2 = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 28,
  letterSpacing: '-.02em',
};

const cta = {
  borderRadius: '16px',
  p: '15px 22px',
  fontFamily: UI,
  fontSize: 16,
  fontWeight: 800,
  textDecoration: 'none',
};

// The reference's card meta is "No fasting · ~6 hrs" — fasting and
// turnaround, no specimen type. That is just as well: `sample` on the
// curated popular-tests rows is wrong for half of them (Lipid Profile
// and LFT both say "FFPE blocks", Vitamin B12 says "Pericardial Fluid",
// Glucose says "Cerebrospinal Fluid"). Home's PopularPicks does print it
// and so is showing those today. API-NOTES 2.28.
const metaFor = (t) => [t.fasting ? 'Fasting' : 'No fasting', t.tat]
  .filter(Boolean).join(' · ');

const City = () => {
  const [params, setParams] = useSearchParams();
  const { data: centres = [], isLoading: centresLoading } = useListCentresQuery();
  const { data: tests = [], isLoading: testsLoading } = useListPopularTestsQuery();

  // Cities are whatever we actually have a branch in, so a new centre
  // adds a chip without a deploy. Alphabetical, not the admin's branch
  // order — that order buries Hyderabad, the default, twelfth in a row
  // of thirteen, and a switcher is something people scan by name.
  const cities = useMemo(
    () => Array.from(new Set(centres.map((c) => c.city).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b)),
    [centres],
  );

  const asked = params.get('city');
  const city = (asked && cities.includes(asked))
    ? asked
    : (cities.includes('Hyderabad') ? 'Hyderabad' : cities[0] || '');

  // A ?city= we don't serve shouldn't leave the URL lying: rewrite it to
  // whatever we actually showed.
  useEffect(() => {
    if (city && asked !== city && cities.length) {
      setParams({ city }, { replace: true });
    }
  }, [city, asked, cities.length, setParams]);

  const pick = (c) => setParams({ city: c }, { replace: false });

  const branches = useMemo(
    () => centres.filter((c) => c.city === city),
    [centres, city],
  );

  const state = STATE_BY_CITY[city];

  const stats = [
    [ARRIVAL, 'Typical Trustian arrival'],
    ['~6 hrs', 'Routine report on WhatsApp'],
    [String(branches.length), branches.length === 1 ? 'Care Centre in the city' : 'Care Centres in the city'],
    ['1,765', 'Tests you can book'],
  ];

  const faqs = city ? [
    {
      q: `Do you offer home sample collection in ${city}?`,
      a: `Yes. Enter your pincode when you book and we will confirm it straight away. A Trustian usually reaches you in about ${ARRIVAL}.`,
    },
    {
      q: `Where are samples from ${city} tested?`,
      a: 'Routine tests run at our NABL-accredited lab network. Specialty and genomics tests run at our central lab in Hyderabad.',
    },
    {
      q: `Can I book for my parents in ${city} from another city?`,
      a: 'Yes. Add them as a family member, enter their address and pay from your account. The report comes to both of you.',
    },
  ] : [];

  return (
    <Box component="main" sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* ── 1. hero ──────────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.75,
            p: 'clamp(28px, 5vw, 56px)',
            borderRadius: '36px',
            bgcolor: '#FFFFFF',
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {centresLoading && [0, 1, 2, 3, 4, 5].map((i) => (
              <Box key={i} sx={{ width: 92, height: 36, borderRadius: '999px', bgcolor: 'var(--tl-mint)' }} />
            ))}
            {cities.map((c) => {
              const on = c === city;
              return (
                <Box
                  key={c}
                  component="button"
                  type="button"
                  onClick={() => pick(c)}
                  aria-pressed={on}
                  sx={{
                    border: 0,
                    cursor: 'pointer',
                    borderRadius: '999px',
                    p: '9px 14px',
                    fontFamily: UI,
                    fontSize: 14,
                    fontWeight: 700,
                    bgcolor: on ? 'var(--tl-g9)' : 'var(--tl-mint)',
                    color: on ? '#FFFFFF' : 'var(--tl-g9)',
                  }}
                >
                  {c}
                </Box>
              );
            })}
          </Box>

          {state && (
            <Box
              component="span"
              sx={{
                alignSelf: 'start',
                p: '7px 12px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-mint)',
                color: 'var(--tl-teal-d)',
                fontFamily: UI,
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              {state}
            </Box>
          )}

          <Typography
            component="h1"
            sx={{
              m: 0,
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: 'clamp(34px, 5.6vw, 76px)',
              lineHeight: 0.95,
              letterSpacing: '-.035em',
              textWrap: 'balance',
              color: 'var(--tl-g9)',
            }}
          >
            Blood tests at home in{' '}
            <Box component="span" sx={{ color: 'var(--tl-teal)' }}>{city || 'your city'}</Box>
          </Typography>

          <Typography sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: 'var(--tl-muted)', maxWidth: 620 }}>
            A Trustian at your door in about {ARRIVAL}
            {city ? ` across ${city}` : ''}. Doctor-verified reports on WhatsApp, the same day.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Box component={Link} to={ROUTES.BOOKING} sx={{ ...cta, bgcolor: 'var(--tl-g9)', color: '#FFFFFF' }}>
              Book a home visit
            </Box>
            <Box component={Link} to={ROUTES.TESTS} sx={{ ...cta, bgcolor: 'var(--tl-mint)', color: 'var(--tl-g9)' }}>
              Find a test
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
              gap: 1.25,
              pt: 2.5,
              borderTop: '1px solid #E1ECE8',
            }}
          >
            {stats.map(([v, l]) => (
              <Box key={l} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 28, color: 'var(--tl-g9)' }}>
                  {v}
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted-2)' }}>{l}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── 2. popular tests ───────────────────────────────────────── */}
      <Box
        component="section"
        sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 7, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Typography component="h2" sx={{ ...h2, px: 1 }}>
          Popular tests{city ? ` in ${city}` : ''}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
            gap: 1.25,
          }}
        >
          {testsLoading && [0, 1, 2, 3, 4, 5].map((i) => (
            <Box key={i} sx={{ height: 78, borderRadius: '22px', bgcolor: '#FFFFFF' }} />
          ))}
          {tests.map((t) => (
            <Box
              key={t.id}
              component={Link}
              to={buildRoute(ROUTES.TEST_DETAIL, { id: t.id })}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1.5,
                p: '18px 20px',
                borderRadius: '22px',
                bgcolor: '#FFFFFF',
                color: 'var(--tl-g9)',
                textDecoration: 'none',
                transition: 'box-shadow .2s var(--tl-ease)',
                '&:hover': { boxShadow: '0 12px 30px -16px rgba(11,43,39,0.35)' },
              }}
            >
              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 16 }}>{t.name}</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>{metaFor(t)}</Box>
              </Box>
              <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 20, whiteSpace: 'nowrap' }}>
                ₹ {Number(t.price).toLocaleString('en-IN')}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── 3. care centres / home collection ──────────────────────── */}
      <Box
        component="section"
        sx={{
          width: '100%',
          maxWidth: 1320,
          mx: 'auto',
          px: 2,
          pt: 7,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, p: 'clamp(22px, 3.4vw, 32px)', borderRadius: '28px', bgcolor: '#FFFFFF' }}>
          <Typography component="h2" sx={{ ...cardH2, color: 'var(--tl-g9)' }}>Care Centres</Typography>
          {branches.length === 0 && !centresLoading && (
            <Box component="span" sx={{ fontFamily: UI, fontSize: 15, color: 'var(--tl-muted)' }}>
              No walk-in centre here yet — home collection still covers this city.
            </Box>
          )}
          {branches.map((b) => (
            <Box key={b._id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 1.75, borderTop: '1px solid #E1ECE8' }}>
              <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 16, color: 'var(--tl-g9)' }}>{b.name}</Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted)' }}>{b.addr}</Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>{b.hours}</Box>
            </Box>
          ))}
          <Box component={Link} to={ROUTES.CENTRES} sx={{ fontFamily: UI, fontWeight: 800, fontSize: 15, color: 'var(--tl-teal-d)', textDecoration: 'none' }}>
            See on map →
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, p: 'clamp(22px, 3.4vw, 32px)', borderRadius: '28px', bgcolor: 'var(--tl-g9)', color: '#FFFFFF' }}>
          <Typography component="h2" sx={{ ...cardH2, color: '#FFFFFF' }}>Home collection areas</Typography>
          {/* The reference prints a dozen invented locality names here and
              labels them "placeholder, to be confirmed" in its own caption.
              We have no area data for any city, so the card asks for the
              pincode that actually decides it. API-NOTES 2.27. */}
          <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: '#C7DDD7' }}>
            We collect across most of {city || 'the city'}. Coverage is decided by pincode, so
            enter yours when you book and we will confirm it before you pay.
          </Box>
          <Box
            component={Link}
            to={ROUTES.BOOKING}
            sx={{ ...cta, alignSelf: 'start', bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)' }}
          >
            Check my pincode
          </Box>
        </Box>
      </Box>

      {/* ── 4. city questions ──────────────────────────────────────── */}
      <Box
        component="section"
        sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 7, pb: 9, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <Typography component="h2" sx={{ ...h2, px: 1 }}>
          Questions{city ? ` from ${city}` : ''}
        </Typography>
        {faqs.map((f) => (
          <Box key={f.q} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: '18px 22px', borderRadius: '20px', bgcolor: '#FFFFFF' }}>
            <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 16, color: 'var(--tl-g9)' }}>{f.q}</Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55, color: 'var(--tl-muted)' }}>{f.a}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default City;
